import React, { useEffect, useState } from "react";
import { IconX } from "@tabler/icons-react";
import { useCRM } from "../CRMContext";
import { TAGS_URL } from "../../../../api/Urls";
import { api } from "../../../../api/Middleware";

const PRIORITIES = [
  { value: "high", label: "Alta" },
  { value: "medium", label: "Média" },
  { value: "low", label: "Baixa" },
];

const TEMPERATURES = [
  { value: "hot", label: "Quente" },
  { value: "warm", label: "Morno" },
  { value: "cold", label: "Frio" },
];

const FilterPanel = () => {
  const { state, setFilters, clearFilters, toggleFilterPanel, refreshBoard } = useCRM();
  const { filters, filterPanelOpen, teamMembers, activePipelineId } = state;

  const [localFilters, setLocalFilters] = useState(filters);
  const [availableTags, setAvailableTags] = useState([]);

  const updateLocal = (key, value) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value || undefined }));
  };

  const toggleTag = (tagId) => {
    setLocalFilters((prev) => {
      const current = Array.isArray(prev.tags) ? prev.tags : [];
      const next = current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : [...current, tagId];
      return { ...prev, tags: next.length > 0 ? next : undefined };
    });
  };

  // Carregar tags do pipeline atual (account-wide + presets) ao abrir
  useEffect(() => {
    if (!filterPanelOpen) return;
    const url = activePipelineId
      ? `${TAGS_URL}?pipeline=${activePipelineId}`
      : TAGS_URL;
    api
      .get(url)
      .then((res) => {
        const data = res.data;
        setAvailableTags(data?.results || data || []);
      })
      .catch(() => setAvailableTags([]));
  }, [filterPanelOpen, activePipelineId]);

  const handleApply = () => {
    // Remove empty values
    const cleaned = {};
    Object.entries(localFilters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") cleaned[k] = v;
    });
    setFilters(cleaned);
    refreshBoard(cleaned);
  };

  const handleClear = () => {
    setLocalFilters({});
    clearFilters();
    refreshBoard({});
  };

  if (!filterPanelOpen) return null;

  return (
    <div className="crm-filters">
      <div className="crm-filters__header">
        <h3 className="crm-filters__title">Filtros</h3>
        <button className="crm-drawer__close" onClick={toggleFilterPanel}>
          <IconX size={18} />
        </button>
      </div>

      {/* Owner */}
      <div className="crm-filters__section">
        <div className="crm-filters__section-title">Responsável</div>
        <select
          className="crm-select"
          value={localFilters.owner || ""}
          onChange={(e) => updateLocal("owner", e.target.value)}
        >
          <option value="">Todos</option>
          {teamMembers.map((m) => (
            <option key={m.id} value={m.id}>
              {m.first_name} {m.last_name}
            </option>
          ))}
        </select>
      </div>

      {/* Priority */}
      <div className="crm-filters__section">
        <div className="crm-filters__section-title">Prioridade</div>
        {PRIORITIES.map((p) => (
          <label key={p.value} className="crm-checkbox">
            <input
              type="checkbox"
              checked={localFilters.priority === p.value}
              onChange={() =>
                updateLocal("priority", localFilters.priority === p.value ? "" : p.value)
              }
            />
            {p.label}
          </label>
        ))}
      </div>

      {/* Temperature */}
      <div className="crm-filters__section">
        <div className="crm-filters__section-title">Temperatura</div>
        {TEMPERATURES.map((t) => (
          <label key={t.value} className="crm-checkbox">
            <input
              type="checkbox"
              checked={localFilters.temperature === t.value}
              onChange={() =>
                updateLocal("temperature", localFilters.temperature === t.value ? "" : t.value)
              }
            />
            {t.label}
          </label>
        ))}
      </div>

      {/* Deal Value Range */}
      <div className="crm-filters__section">
        <div className="crm-filters__section-title">Valor do Negócio</div>
        <div className="crm-form-row">
          <input
            type="number"
            className="crm-input"
            placeholder="Mín"
            value={localFilters.value_min || ""}
            onChange={(e) => updateLocal("value_min", e.target.value)}
          />
          <input
            type="number"
            className="crm-input"
            placeholder="Máx"
            value={localFilters.value_max || ""}
            onChange={(e) => updateLocal("value_max", e.target.value)}
          />
        </div>
      </div>

      {/* Tags */}
      <div className="crm-filters__section">
        <div className="crm-filters__section-title">Tags</div>
        {availableTags.length === 0 ? (
          <div style={{ fontSize: 12, color: "#94a3b8", padding: "4px 0" }}>
            Nenhuma tag disponível neste pipeline.
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {availableTags.map((tag) => {
              const selected = (localFilters.tags || []).includes(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(tag.id)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "3px 10px",
                    borderRadius: 12,
                    background: selected ? (tag.color || "#3b82f6") : "transparent",
                    color: selected ? "#fff" : (tag.color || "#475569"),
                    border: `1px solid ${tag.color || "#cbd5e1"}`,
                    fontSize: 12,
                    fontWeight: selected ? 600 : 400,
                    cursor: "pointer",
                  }}
                >
                  {tag.name}
                </button>
              );
            })}
          </div>
        )}
        {(localFilters.tags || []).length > 1 && (
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 6 }}>
            Filtro OR: contatos com qualquer uma das {(localFilters.tags || []).length} tags selecionadas.
          </div>
        )}
      </div>

      {/* Stagnant */}
      <div className="crm-filters__section">
        <label className="crm-checkbox">
          <input
            type="checkbox"
            checked={localFilters.stagnant === "true"}
            onChange={() =>
              updateLocal("stagnant", localFilters.stagnant === "true" ? "" : "true")
            }
          />
          Apenas estagnados
        </label>
      </div>

      {/* Sort */}
      <div className="crm-filters__section">
        <div className="crm-filters__section-title">Ordenar por</div>
        <select
          className="crm-select"
          value={localFilters.sort_by || ""}
          onChange={(e) => updateLocal("sort_by", e.target.value)}
        >
          <option value="">Padrão</option>
          <option value="-deal_value">Valor (maior)</option>
          <option value="deal_value">Valor (menor)</option>
          <option value="-created_at">Mais recentes</option>
          <option value="created_at">Mais antigos</option>
          <option value="name">Nome (A-Z)</option>
          <option value="-name">Nome (Z-A)</option>
          <option value="-priority">Prioridade (maior)</option>
          <option value="stage_entered_at">Há mais tempo no estágio</option>
        </select>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, marginTop: "auto" }}>
        <button className="crm-btn" onClick={handleClear} style={{ flex: 1 }}>
          Limpar
        </button>
        <button className="crm-btn crm-btn--primary" onClick={handleApply} style={{ flex: 1 }}>
          Aplicar
        </button>
      </div>
    </div>
  );
};

export default FilterPanel;
